import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type {
  AllIssueDataForSettingsQuery,
  IssueRiskAspectNode,
} from '@/modules/issues'
import {
  ALL_ISSUE_DATA_FOR_SETTINGS_QUERY,
  CREATE_SUBCATEGORY,
  PATCH_SUBCATEGORY,
} from '@/modules/issues'
import type { ShallowUserNode } from '@/modules/users'
import React, { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import type {
  IssueCategoriesSettingsForm,
  IssueCategoryForSettings,
} from '../components'
import { IssueCategorySettingsCard } from '../components'
import {
  OldAddButton,
  InfoBox,
  OldSection as BaseSection,
  SectionHeader,
  SettingsWrapper,
} from '../components/common'
import type { IssueCategoryForm } from '../components/IssueCategorySettings'
import {
  IssueCategoryPrompt,
  IssueFormFieldsSection,
  IssueTableSettingsSection,
  IssueProcessSection,
  IssueCategorySettingsTable,
  IssueTagSection,
} from '../components/IssueCategorySettings'
import { SelectUserPrompt } from '../components/SelectUserPrompt'
import {
  useIssueCategoryMutations,
  useIssueRelationMutations,
  useIssueRiskAspectMutations,
} from '../util/mutations.hooks'
import { DropdownNames } from '@/components_deprecated/Select/types'
import type { IssueRiskAspectForm } from '../components/IssueCategorySettings/IssueRiskAspectPrompt'
import { IssueRiskAspectPrompt } from '../components/IssueCategorySettings/IssueRiskAspectPrompt'
import type {
  IssueRelationForm,
  IssueRelationOptionFormItem,
} from '../components/IssueCategorySettings/issue-relation-prompt'
import { IssueRelationPrompt } from '../components/IssueCategorySettings/issue-relation-prompt'
import { Table } from '@/components_deprecated/Table'
import { Module } from '@/modules/companies/consts'
import { ModuleOptionCards } from '../components/ModuleOptionCards'
import { getNodes } from '@/utils/graphql'

export const LS_KEY_LIST_VIEW_MODE_ISSUE_CATEGORIES_SETTINGS =
  'firmadok-list-view-mode-issue-categories-settings'

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`
const Section = styled(BaseSection)`
  h4 {
    margin: 0;
  }

  ul {
    margin: 0;
    padding-left: 1rem;
  }
`

interface IssueSettingsProps {}

export const IssueSettings: React.FC<IssueSettingsProps> = () => {
  const translations = useTranslate({
    info1: 'settings.info.issue-categories-1',
    info2: 'settings.info.issue-categories-2',

    issueCategories: 'common.issue-categories',
    issueRiskAspect: 'common.risk-aspect',
    issueRiskAspects: 'common.risk-aspects',

    issueRelations: 'settings.issue-relations',
    issueRelation: 'settings.issue-relation',
    type: 'common.type',
    singleChoice: 'settings.issue-relation-single-choice',
    multipleChoice: 'settings.issue-relation-multiple-choice',
    options: 'settings.issue-relation-options',
    optionCount: ['settings.issue-relation-option-count', { count: 0 }],

    validation: {
      nameExists: 'settings.validation.issue-category-name-exists',
    },
    moduleOptions: 'settings.module-options',
    prompt: {
      deleteCategory: ['settings.prompts.issue-category-delete', { name: '' }],
      deleteCategoryTitle: 'settings.prompts.issue-category-delete-title',
      deleteRiskAspect: [
        'settings.prompts.issue-risk-aspect-delete',
        { name: '' },
      ],
      deleteRiskAspectTitle: 'settings.prompts.issue-risk-aspect-delete-title',
      deleteRelation: ['settings.prompts.issue-relation-delete', { name: '' }],
      deleteRelationTitle: 'settings.prompts.issue-relation-delete-title',
    },
  })

  const confirm = useConfirm()

  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [temporaryEditColor, setTemporaryEditColor] = useState<{
    [id: string]: string | null
  }>({})

  const {
    formValues: form,
    formChangeHandler: handler,
    updateForm,
  } = useForm<IssueCategoriesSettingsForm>({
    values: {
      newName: '',
      newColor: 'hsl(138deg, 80%, 80%)',
      editName: '',
      defaultSeverity: 'MEDIUM',
      responsibleUser: null,
      caseHandler: null,
    },
  })

  // TODO fix exhaustive-deps?
  // eslint-disable-next-line
  const updateFormCallback = useCallback(updateForm, [])
  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()
  const addPrompt = usePrompt()

  const {
    data: queryData,
    loading: queryLoading,
    refetch,
  } = useQuery<AllIssueDataForSettingsQuery, never>(
    ALL_ISSUE_DATA_FOR_SETTINGS_QUERY,
    {
      onError: onErrorAuto(),
    }
  )

  const categories = getNodes(queryData?.allIssueCategories)

  const riskAspects =
    queryData?.allIssueRiskAspects?.edges.map(edge => edge.node) ?? []

  const relations =
    queryData?.allIssueRelations?.edges.map(edge => edge.node) ?? []

  useEffect(() => {
    if (!adding) {
      updateFormCallback({
        newName: '',
        newColor: 'hsl(138deg, 80%, 80%)',
        responsibleUser: null,
        caseHandler: null,
      })
    }
  }, [adding, updateFormCallback])

  useEffect(() => {
    if (editing === null)
      updateFormCallback({
        editName: '',
        responsibleUser: null,
        caseHandler: null,
      })
  }, [editing, updateFormCallback])

  const mutations = useIssueCategoryMutations()
  const riskAspectMutations = useIssueRiskAspectMutations()
  const relationMutations = useIssueRelationMutations()

  function validateName(name: string) {
    if (name === '') return false
    if (categories.find(type => type.name === form.newName)) {
      addToast('warning', translations.validation.nameExists)
      return false
    }
    return true
  }

  function checkNameHasChanged(id: string) {
    const category = categories.find(type => type.id === id)
    if (typeof category === 'undefined') return true

    return category.name !== form.editName
  }

  async function submitNewIssueCategory() {
    if (!validateName(form.newName)) return

    await mutations.create({
      variables: {
        input: {
          name: form.newName,
          color: form.newColor,
          defaultSeverity: form.defaultSeverity,
          responsibleUser: form.responsibleUser?.id,
          caseHandler: form.caseHandler?.id,
        },
      },
    })
    setAdding(false)
  }

  async function submitEditIssueCategoryName() {
    if (!validateName(form.editName) || editing === null) return
    if (!checkNameHasChanged(editing)) {
      setEditing(null)
      return
    }

    await mutations.patch(true)({
      variables: {
        id: editing,
        input: { name: form.editName },
      },
    })
    setEditing(null)
  }

  function handleUpdateColor(id: string) {
    return async (color: string) => {
      setTemporaryEditColor(v => ({
        ...v,
        [id]: color,
      }))

      try {
        await mutations.patch(false)({
          variables: { id, input: { color } },
        })
      } finally {
        setTemporaryEditColor(v => ({
          ...v,
          [id]: null,
        }))
      }
    }
  }

  async function handleDeleteCategoryPrompt(
    category: IssueCategoryForSettings
  ) {
    const { data: answer } = await confirm(
      translations.prompt.deleteCategory({ name: category.name }),
      translations.prompt.deleteCategoryTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: {
        id: category.id,
      },
    })
  }

  async function handleDeleteRiskAspectPrompt(riskAspect: IssueRiskAspectNode) {
    const { data: answer } = await confirm(
      translations.prompt.deleteRiskAspect({ name: riskAspect.name }),
      translations.prompt.deleteRiskAspectTitle
    )
    if (!answer) return

    void riskAspectMutations.delete({
      variables: {
        id: riskAspect.id,
      },
    })
  }

  async function handleResponsibleUserPrompt(
    categoryId: string | null,
    responsibleUser: ShallowUserNode | null
  ) {
    updateForm({ responsibleUser })

    const { data: user } = await addPrompt<typeof responsibleUser>(resolve => (
      <SelectUserPrompt
        user={responsibleUser}
        dropdownPageName={DropdownNames.ISSUES}
        onResolve={resolve}
      />
    ))
    if (user === null) return

    if (user.id === responsibleUser?.id) {
      updateForm({ responsibleUser: null })
      setEditing(null)
      return
    }

    if (categoryId === null) updateForm({ responsibleUser: user })
    else void handleSubmitResponsibleUser(categoryId, user)
  }

  async function handleSubmitResponsibleUser(
    categoryId: string,
    responsibleUser: ShallowUserNode
  ) {
    await mutations.patch(true)({
      variables: {
        id: categoryId,
        input: {
          responsibleUser: responsibleUser.id,
        },
      },
    })
    updateForm({ responsibleUser: null })
    setEditing(null)
  }

  async function handleCaseHandlerPrompt(
    categoryId: string | null,
    caseHandler: ShallowUserNode | null
  ) {
    updateForm({ caseHandler })

    const { data: user } = await addPrompt<typeof caseHandler>(resolve => (
      <SelectUserPrompt
        user={caseHandler}
        dropdownPageName={DropdownNames.ISSUES}
        onResolve={resolve}
      />
    ))
    if (user === null) return

    if (user.id === caseHandler?.id) {
      updateForm({ caseHandler: null })
      setEditing(null)
      return
    }

    if (categoryId === null) updateForm({ caseHandler: user })
    else void handleSubmitCaseHandler(categoryId, user)
  }

  async function handleSubmitCaseHandler(
    categoryId: string,
    caseHandler: ShallowUserNode
  ) {
    await mutations.patch(true)({
      variables: {
        id: categoryId,
        input: {
          caseHandler: caseHandler.id,
        },
      },
    })
    updateForm({ caseHandler: null })
    setEditing(null)
  }

  const isLoading =
    queryLoading ||
    mutations.loading ||
    riskAspectMutations.loading ||
    relationMutations.loading

  type RelationNode = (typeof relations)[number]

  function getRelationOptions(
    relation: RelationNode
  ): IssueRelationOptionFormItem[] {
    return (
      relation.options?.edges?.map(edge => ({
        id: edge.node.id,
        name: edge.node.name,
      })) ?? []
    )
  }

  async function handleCreateRelationPrompt() {
    const { data } = await addPrompt<IssueRelationForm | null>(resolve => (
      <IssueRelationPrompt
        existingRelations={relations.map(r => ({ id: r.id, name: r.name }))}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    const { data: createResp } = await relationMutations.create({
      variables: {
        input: {
          name: data.name,
          multipleChoice: data.multipleChoice,
        },
      },
    })
    const relationId = createResp?.createIssueRelation?.issueRelation?.id
    if (!relationId) return

    await Promise.all(
      data.options.map(option =>
        relationMutations.createOption({
          variables: {
            input: { relation: relationId, name: option.name },
          },
        })
      )
    )
    await refetch()
  }

  async function handleEditRelationPrompt(relation: RelationNode) {
    const original = getRelationOptions(relation)
    const initialData: IssueRelationForm = {
      name: relation.name,
      multipleChoice: relation.multipleChoice,
      options: original,
    }

    const { data } = await addPrompt<IssueRelationForm | null>(resolve => (
      <IssueRelationPrompt
        initialData={initialData}
        relationId={relation.id}
        existingRelations={relations.map(r => ({ id: r.id, name: r.name }))}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    await relationMutations.patch({
      variables: {
        id: relation.id,
        input: { name: data.name, multipleChoice: data.multipleChoice },
      },
    })

    const submittedIds = new Set(
      data.options.filter(o => o.id).map(o => o.id as string)
    )
    const removedIds = original
      .filter(o => o.id && !submittedIds.has(o.id))
      .map(o => o.id as string)

    await Promise.all([
      ...removedIds.map(id =>
        relationMutations.deleteOption({ variables: { id } })
      ),
      ...data.options.map(option => {
        if (option.id) {
          const existing = original.find(o => o.id === option.id)
          if (existing && existing.name === option.name) return null
          return relationMutations.patchOption({
            variables: { id: option.id, input: { name: option.name } },
          })
        }
        return relationMutations.createOption({
          variables: {
            input: { relation: relation.id, name: option.name },
          },
        })
      }),
    ])

    await refetch()
  }

  async function handleDeleteRelationPrompt(relation: RelationNode) {
    const { data: answer } = await confirm(
      translations.prompt.deleteRelation({ name: relation.name }),
      translations.prompt.deleteRelationTitle
    )
    if (!answer) return

    void relationMutations.delete({ variables: { id: relation.id } })
  }

  const [createSubcategory] = useMutation(CREATE_SUBCATEGORY)
  const [patchSubcategory] = useMutation(PATCH_SUBCATEGORY)

  async function handleCreateCategoryPrompt() {
    const { data } = await addPrompt<IssueCategoryForm | null>(resolve => (
      <IssueCategoryPrompt categories={categories} onSubmit={resolve} />
    ))
    if (data === null) return
    const { data: categoryResp } = await mutations.create({
      variables: {
        input: {
          name: data.name,
          color: data.color,
          responsibleUser: data.responsibleUser,
          caseHandler: data.caseHandler,
          automaticReminderInterval: data.automaticReminderInterval,
          minimumSeverityForReminder: data.minimumSeverityForReminder,
          remindResponsible: data.remindResponsible,
          remindCreator: data.remindCreator,
          defaultExportText: data.defaultExportText,
        },
      },
    })
    const categoryId = categoryResp?.createIssueCategory?.issueCategory?.id
    if (!categoryId) return

    await Promise.all(
      data?.subcategories?.map(subcategory =>
        createSubcategory({
          variables: {
            input: {
              category: categoryId,
              name: subcategory.name,
              defaultSeverity: subcategory.defaultSeverity,
              responsibleUser: data.responsibleUser,
              caseHandler: data.caseHandler,
            },
          },
        })
      )
    )
    await refetch()
  }

  async function handleCreateRiskAspectPrompt() {
    const { data } = await addPrompt<IssueRiskAspectForm | null>(resolve => (
      <IssueRiskAspectPrompt
        existingRiskAspects={riskAspects}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void riskAspectMutations.create({
      variables: {
        input: { ...data },
      },
    })
  }

  async function handleEditCategoryPrompt(category: IssueCategoryForSettings) {
    const initialData: IssueCategoryForm = {
      name: category.name,
      color: category.color,
      responsibleUser: category.responsibleUser?.id ?? null,
      caseHandler: category.caseHandler?.id ?? null,
      automaticReminderInterval: category.automaticReminderInterval,
      minimumSeverityForReminder: category.minimumSeverityForReminder,
      remindResponsible: category.remindResponsible,
      remindCreator: category.remindCreator,
      defaultExportText: category.defaultExportText ?? '',
      subcategories:
        category.issueSubcategories?.edges?.map(el => el.node) ?? [],
      newSubCategory: {
        name: '',
        defaultSeverity: 'MEDIUM',
      },
    }
    const { data } = await addPrompt<IssueCategoryForm | null>(resolve => (
      <IssueCategoryPrompt
        category={category}
        initialData={initialData}
        categories={categories}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    const { data: categoryResp } = await mutations.patch(true)({
      variables: {
        id: category.id,
        input: {
          name: data.name,
          color: data.color,
          responsibleUser: data.responsibleUser,
          caseHandler: data.caseHandler,
          automaticReminderInterval: data.automaticReminderInterval,
          minimumSeverityForReminder: data.minimumSeverityForReminder,
          remindResponsible: data.remindResponsible,
          remindCreator: data.remindCreator,
          defaultExportText: data.defaultExportText,
        },
      },
    })
    const categoryId = categoryResp?.patchIssueCategory?.issueCategory?.id
    if (!categoryId) return

    await Promise.all(
      data?.subcategories?.map(subcategory => {
        if (subcategory?.id) {
          void patchSubcategory({
            variables: {
              id: subcategory.id,
              input: {
                name: subcategory.name,
                category: categoryId,
                defaultSeverity: subcategory.defaultSeverity,
                responsibleUser: data.responsibleUser,
                caseHandler: data.caseHandler,
              },
            },
          })
        } else {
          void createSubcategory({
            variables: {
              input: {
                category: categoryId,
                name: subcategory.name,
                defaultSeverity: subcategory.defaultSeverity,
                responsibleUser: data.responsibleUser,
                caseHandler: data.caseHandler,
              },
            },
          })
        }
        return true
      })
    )
    await refetch()
  }

  async function handleEditRiskAspectPrompt(riskAspect: IssueRiskAspectNode) {
    const initialData: IssueRiskAspectForm = {
      name: riskAspect.name,
    }
    const { data } = await addPrompt<IssueRiskAspectForm | null>(resolve => (
      <IssueRiskAspectPrompt
        initialData={initialData}
        existingRiskAspects={riskAspects}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void riskAspectMutations.patch({
      variables: {
        id: riskAspect.id,
        input: data,
      },
    })
  }

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info1}</p>
        <p>{translations.info2}</p>
      </InfoBox>
      <Section>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.issueRiskAspects}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton
              adding={adding}
              onClick={handleCreateRiskAspectPrompt}
            />
          )}
        </Header>
        {riskAspects.length > 0 && (
          <Table
            noShadow
            noBorders
            noHeaderMargin
            noRowMargin
            rowHoverColor="secondary"
          >
            <thead>
              <tr>
                <th>{translations.issueRiskAspect}</th>
                <th colSpan={2} />
              </tr>
            </thead>
            <tbody>
              {riskAspects.map(aspect => (
                <tr key={aspect.id}>
                  <td>{aspect.name}</td>
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEditRiskAspectPrompt(aspect)}
                    />
                  </td>
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDeleteRiskAspectPrompt(aspect)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
      <Section>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.issueRelations}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton adding={false} onClick={handleCreateRelationPrompt} />
          )}
        </Header>
        {relations.length > 0 && (
          <Table
            noShadow
            noBorders
            noHeaderMargin
            noRowMargin
            rowHoverColor="secondary"
          >
            <thead>
              <tr>
                <th>{translations.issueRelation}</th>
                <th>{translations.type}</th>
                <th>{translations.options}</th>
                <th colSpan={2} />
              </tr>
            </thead>
            <tbody>
              {relations.map(relation => {
                const opts = getRelationOptions(relation)
                const optionNames = opts.map(o => o.name).join(', ')
                return (
                  <tr key={relation.id}>
                    <td>{relation.name}</td>
                    <td>
                      {relation.multipleChoice
                        ? translations.multipleChoice
                        : translations.singleChoice}
                    </td>
                    <td title={optionNames}>
                      {translations.optionCount({ count: opts.length })}
                    </td>
                    <td width="1px">
                      <Icon
                        icon="edit"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="secondary"
                        onClick={() => handleEditRelationPrompt(relation)}
                      />
                    </td>
                    <td width="1px">
                      <Icon
                        icon="times"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="red"
                        onClick={() => handleDeleteRelationPrompt(relation)}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Section>
      <IssueProcessSection loading={isLoading} />
      <IssueTagSection loading={isLoading} />
      <IssueFormFieldsSection />
      <IssueTableSettingsSection />
      <Section>
        <Header>
          <SectionHeader>{translations.moduleOptions}</SectionHeader>
        </Header>
        <ModuleOptionCards module={Module.ISSUES} />
      </Section>
      <Section>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.issueCategories}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton
              adding={adding}
              onClick={handleCreateCategoryPrompt}
            />
          )}
        </Header>

        {isMobileOnly ? (
          <IssueCategorySettingsCard
            categories={categories}
            form={form}
            adding={adding}
            editing={editing}
            temporaryEditColor={temporaryEditColor}
            onFormChange={handler}
            onUpdateForm={updateForm}
            onSelectResponsibleUser={handleResponsibleUserPrompt}
            onSelectCaseHandler={handleCaseHandlerPrompt}
            onUpdateColor={handleUpdateColor}
            onAdd={setAdding}
            onAddSubmit={submitNewIssueCategory}
            onEdit={setEditing}
            onEditSubmit={submitEditIssueCategoryName}
            onDelete={handleDeleteCategoryPrompt}
          />
        ) : (
          <IssueCategorySettingsTable
            categories={categories}
            form={form}
            temporaryEditColor={temporaryEditColor}
            onSelectResponsibleUser={handleResponsibleUserPrompt}
            onSelectCaseHandler={handleCaseHandlerPrompt}
            onUpdateColor={handleUpdateColor}
            onEditClick={handleEditCategoryPrompt}
            onDelete={handleDeleteCategoryPrompt}
          />
        )}
      </Section>
    </SettingsWrapper>
  )
}
